import {
  CaretRightIcon,
  CheckCircleIcon,
  InfoIcon,
  WarningIcon,
  XCircleIcon,
} from '@phosphor-icons/react';

import { AiActionButton } from './AiActionButton';
import { biggestGaps, groupScores, scoreBand } from './critique-judgment';

import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Skeleton } from '@/components/ui/skeleton';
import { StatusBadge } from '@/components/ui/status-badge';
import { type ConceptAdCritiqueTarget, useConceptAdCritique } from '@/hooks/useConceptAdCritique';
import {
  type AdCritiqueResult,
  CRITIQUE_DIMENSIONS,
  type CritiqueActionId,
  type CritiqueCheck,
  type CritiqueScore,
  actionForFinding,
  actionForScore,
  groupCritiqueActions,
  summariseChecks,
} from '@/lib/concept-workbench/ad-critique';
import { ICON_BUTTON, ITEM_META, SECTION_LABEL } from '@/lib/ui-tokens';
import { cn } from '@/lib/utils';

/**
 * Handlers for the controls that already exist on the design page. An action
 * whose handler is absent renders no link at all, rather than a dead one.
 */
export interface CritiqueActionHandlers {
  /** Scrolls to the Content section, where copy is written. */
  onWriteCopy?: () => void;
  /** Puts the improvement in the image prompt bar. The person still presses send. */
  onEditImage?: (suggestion: string) => void;
  onGenerateSize?: () => void;
}

export interface ConceptCritiqueSectionProps extends ConceptAdCritiqueTarget {
  actions?: CritiqueActionHandlers;
  className?: string;
}

const ACTION_LABELS: Record<CritiqueActionId, string> = {
  write_copy: 'Write more copy options',
  edit_image: 'Edit the image with a prompt',
  generate_size: 'Generate this size',
};

const STATUS_ICON = {
  pass: { Icon: CheckCircleIcon, className: 'text-success', label: 'Passed' },
  fail: { Icon: XCircleIcon, className: 'text-destructive', label: 'Failed' },
  warn: { Icon: WarningIcon, className: 'text-warning', label: 'Warning' },
} as const;

function handlerFor(
  action: CritiqueActionId,
  handlers: CritiqueActionHandlers | undefined,
  suggestion?: string | null,
): (() => void) | undefined {
  if (!handlers) return undefined;
  if (action === 'write_copy') return handlers.onWriteCopy;
  if (action === 'generate_size') return handlers.onGenerateSize;
  return handlers.onEditImage ? () => handlers.onEditImage?.(suggestion ?? '') : undefined;
}

function ActionLink({
  action,
  suggestion,
  handlers,
}: {
  action: CritiqueActionId | null;
  suggestion?: string | null;
  handlers?: CritiqueActionHandlers;
}): JSX.Element | null {
  if (!action) return null;
  const onClick = handlerFor(action, handlers, suggestion);
  if (!onClick) return null;
  return (
    <AiActionButton className="self-start" onClick={onClick}>
      {ACTION_LABELS[action]}
    </AiActionButton>
  );
}

function CheckRow({ check }: { check: CritiqueCheck }): JSX.Element {
  const { Icon, className, label } = STATUS_ICON[check.status];
  return (
    <li className="flex gap-2" data-status={check.status}>
      <Icon
        aria-label={label}
        weight="regular"
        className={cn('mt-0.5 size-4 shrink-0', className)}
      />
      <div className="flex min-w-0 flex-col gap-1">
        <p className="text-detail font-medium text-foreground">{check.label}</p>
        <p className="text-detail text-muted-foreground">{check.detail}</p>
        {check.fix ? <p className="text-detail text-foreground/85">{check.fix}</p> : null}
      </div>
    </li>
  );
}

const BAND_STYLE = {
  low: { fill: 'bg-warning', text: 'text-warning-ink', label: 'Low' },
  mid: { fill: 'bg-info', text: 'text-info', label: 'Middling' },
  high: { fill: 'bg-success', text: 'text-success', label: 'Strong' },
} as const;

function ScoreMeter({ score }: { score: number }): JSX.Element {
  const band = BAND_STYLE[scoreBand(score)];
  return (
    <span aria-hidden className="h-1.5 w-14 shrink-0 overflow-hidden rounded-full bg-muted">
      <span
        data-band={scoreBand(score)}
        className={cn('block h-full rounded-full', band.fill)}
        style={{ width: `${Math.max(0, Math.min(10, score)) * 10}%` }}
      />
    </span>
  );
}

function scoreLabel(score: CritiqueScore): string {
  return (
    CRITIQUE_DIMENSIONS.find((dimension) => dimension.id === score.dimension)?.label ?? score.label
  );
}

function ScoreRow({
  score,
  handlers,
}: {
  score: CritiqueScore;
  handlers?: CritiqueActionHandlers;
}): JSX.Element {
  const band = BAND_STYLE[scoreBand(score.score)];
  const action = actionForScore(score);
  return (
    <li>
      <Collapsible>
        <CollapsibleTrigger className="group flex min-h-11 w-full items-center gap-2 rounded-md text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring md:min-h-7">
          <CaretRightIcon
            aria-hidden
            className="size-3 shrink-0 text-muted-foreground transition-transform group-data-[state=open]:rotate-90"
          />
          <span className="min-w-0 flex-1 text-detail font-medium text-foreground">
            {scoreLabel(score)}
          </span>
          <ScoreMeter score={score.score} />
          <span
            className={cn('w-8 shrink-0 text-right text-detail tabular-nums', band.text)}
            aria-label={`${score.score} out of 10, model judgment`}
          >
            {score.score}/10
          </span>
        </CollapsibleTrigger>
        <CollapsibleContent>
          <dl className="flex flex-col gap-2 pb-1 pl-5 pt-1">
            {score.observation ? (
              <div className="flex flex-col gap-0.5">
                <dt className={cn(ITEM_META, 'font-medium text-foreground')}>What it saw</dt>
                <dd className="text-detail text-muted-foreground">{score.observation}</dd>
              </div>
            ) : null}
            {score.improvement ? (
              <div className="flex flex-col gap-0.5">
                <dt className={cn(ITEM_META, 'font-medium text-foreground')}>Try this</dt>
                <dd className="text-detail text-foreground/85">{score.improvement}</dd>
              </div>
            ) : null}
            {score.withheldReason ? <p className={ITEM_META}>{score.withheldReason}</p> : null}
            {action === 'edit_image' ? (
              <ActionLink action={action} suggestion={score.improvement} handlers={handlers} />
            ) : null}
          </dl>
        </CollapsibleContent>
      </Collapsible>
    </li>
  );
}

function HowJudged({ note }: { note: string | null }): JSX.Element {
  return (
    <div className="flex items-start gap-1">
      <p className={cn(ITEM_META, 'flex-1')}>
        A model&apos;s read of taste, never a block. Not a forecast.
      </p>
      <Popover>
        <PopoverTrigger
          aria-label="How this is judged"
          className={cn(
            ICON_BUTTON,
            'flex size-11 shrink-0 items-center justify-center rounded-md text-foreground/60 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring md:size-6',
          )}
        >
          <InfoIcon aria-hidden className="size-4" />
        </PopoverTrigger>
        <PopoverContent align="end" className="flex w-72 flex-col gap-2 p-3">
          {note ? <p className="text-detail text-muted-foreground">{note}</p> : null}
          <p className="text-detail text-muted-foreground">
            A low score is a note, never a block. Each dimension answers one question about this ad:
          </p>
          <ul className="flex flex-col gap-1.5">
            {CRITIQUE_DIMENSIONS.map((dimension) => (
              <li key={dimension.id} className="text-detail text-muted-foreground">
                <span className="font-medium text-foreground">{dimension.label}.</span>{' '}
                {dimension.question}
              </li>
            ))}
          </ul>
        </PopoverContent>
      </Popover>
    </div>
  );
}

function BiggestGaps({ scores }: { scores: readonly CritiqueScore[] }): JSX.Element | null {
  const gaps = biggestGaps(scores);
  if (gaps.length === 0) return null;
  return (
    <div className="flex flex-col gap-1 rounded-lg bg-info/5 px-2.5 py-2" data-testid="gaps">
      <p className={cn(ITEM_META, 'font-medium text-foreground')}>Biggest gaps</p>
      <ul className="flex flex-col gap-0.5" aria-label="Biggest gaps">
        {gaps.map((gap) => (
          <li key={gap.dimension} className="flex items-baseline justify-between gap-2">
            <span className="text-detail text-foreground">{scoreLabel(gap)}</span>
            <span className={cn('text-detail tabular-nums', BAND_STYLE[scoreBand(gap.score)].text)}>
              {gap.score}/10
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
}

function CheckCounts({ counts }: { counts: ReturnType<typeof summariseChecks> }): JSX.Element {
  return (
    <div className="flex flex-wrap items-center gap-1.5">
      {counts.failed > 0 ? (
        <StatusBadge kind="generic" tone="destructive">
          {counts.failed} failed
        </StatusBadge>
      ) : null}
      {counts.warned > 0 ? (
        <StatusBadge kind="generic" tone="warning">
          {counts.warned} to review
        </StatusBadge>
      ) : null}
      {counts.passed > 0 ? (
        <StatusBadge kind="generic" tone="success">
          {counts.passed} passed
        </StatusBadge>
      ) : null}
    </div>
  );
}

function CritiqueBody({
  result,
  stale,
  handlers,
}: {
  result: AdCritiqueResult;
  stale: boolean;
  handlers?: CritiqueActionHandlers;
}): JSX.Element {
  const counts = summariseChecks(result.checks);
  const open = result.checks.filter((check) => check.status !== 'pass');
  const passed = result.checks.filter((check) => check.status === 'pass');
  const groups = groupScores(result.scores);
  const nextSteps = groupCritiqueActions([
    ...open.map((check) => ({ label: check.label, action: actionForFinding(check) })),
    ...result.scores
      .map((score) => ({ label: scoreLabel(score), action: actionForScore(score) }))
      .filter((item) => item.action === 'write_copy'),
  ]).filter((group) => handlerFor(group.action, handlers));
  return (
    <div className="flex flex-col gap-4">
      {stale ? (
        <p className="text-detail text-warning">
          The ad changed since this critique, so it may no longer describe what is on screen.
        </p>
      ) : null}

      <BiggestGaps scores={result.scores} />

      <div className="flex flex-col gap-2">
        <div className="flex items-center justify-between gap-2">
          <p className={SECTION_LABEL}>Checks</p>
          <CheckCounts counts={counts} />
        </div>
        <p className={ITEM_META}>Fixed rules in Mora, not an opinion.</p>
        {open.length > 0 ? (
          <ul className="flex flex-col gap-2.5" aria-label="Checks to address">
            {open.map((check, index) => (
              <CheckRow key={`${check.id}-${index}`} check={check} />
            ))}
          </ul>
        ) : null}
        {passed.length > 0 ? (
          <Collapsible>
            <CollapsibleTrigger className="group flex min-h-11 items-center gap-1 text-detail text-muted-foreground hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring md:min-h-6">
              <CaretRightIcon
                aria-hidden
                className="size-3 transition-transform group-data-[state=open]:rotate-90"
              />
              Show {passed.length} passed
            </CollapsibleTrigger>
            <CollapsibleContent>
              <ul className="mt-2 flex flex-col gap-2.5" aria-label="Checks passed">
                {passed.map((check) => (
                  <CheckRow key={check.id} check={check} />
                ))}
              </ul>
            </CollapsibleContent>
          </Collapsible>
        ) : null}
      </div>

      <div className="flex flex-col gap-2 border-t border-border/40 pt-3">
        <p className={SECTION_LABEL}>Model judgment</p>
        {groups.length > 0 ? (
          <>
            <HowJudged note={result.judge.note} />
            {groups.map((group) => (
              <div key={group.id} className="flex flex-col gap-0.5">
                <p className="text-detail font-medium text-foreground">{group.label}</p>
                <ul className="flex flex-col" aria-label={`${group.label} scores`}>
                  {group.scores.map((score) => (
                    <ScoreRow key={score.dimension} score={score} handlers={handlers} />
                  ))}
                </ul>
              </div>
            ))}
          </>
        ) : (
          <p className={ITEM_META}>{result.judge.note ?? 'Not judged.'}</p>
        )}
      </div>

      {nextSteps.length > 0 ? (
        <div className="flex flex-col gap-1 border-t border-border/40 pt-3">
          <p className={SECTION_LABEL}>Next steps</p>
          <ul className="flex flex-col gap-2" aria-label="Next steps">
            {nextSteps.map((group) => (
              <li key={group.action} className="flex flex-col items-start gap-0.5">
                <ActionLink action={group.action} handlers={handlers} />
                <p className={ITEM_META}>Addresses: {group.addresses.join(', ')}</p>
              </li>
            ))}
          </ul>
        </div>
      ) : null}
    </div>
  );
}

export function ConceptCritiqueSection({
  actions,
  className,
  ...target
}: ConceptCritiqueSectionProps): JSX.Element {
  const { response, isLoading, isRunning, error, canRun, run } = useConceptAdCritique(target);
  const result = response && response.state !== 'none' ? response.result : null;
  const stale = response && response.state !== 'none' ? response.stale : false;

  return (
    <section
      id="concept-critique-section"
      className={cn('flex flex-col gap-2 border-b border-border/40 px-2.5 py-2.5', className)}
    >
      <header className="flex items-center justify-between gap-2">
        <span className={SECTION_LABEL}>Critique</span>
        <AiActionButton disabled={!canRun} busy={isRunning} onClick={run}>
          {isRunning ? 'Critiquing…' : result ? 'Critique again' : 'Critique this ad'}
        </AiActionButton>
      </header>

      {isLoading ? (
        <Skeleton className="h-8 w-full" />
      ) : result ? (
        <CritiqueBody result={result} stale={stale} handlers={actions} />
      ) : (
        <p className="text-detail text-muted-foreground">
          {target.mediaId
            ? "Not critiqued yet. This reads the selected copy, this size's image and the Concept's hook, proof and audience together."
            : 'Not critiqued yet. This size has no image yet, so only the copy is checked and the visual judgment will not run until an image is added.'}
        </p>
      )}

      {error ? (
        <p className="text-detail text-destructive">
          {error instanceof Error ? error.message : 'The critique did not run.'}
        </p>
      ) : null}
    </section>
  );
}
